import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouterState,
} from '@tanstack/react-router';
import { ThemeProvider } from '@/components/theme/theme-provider';
import { DefaultNotFound } from '@/components/layout/default-not-found';
import { Footer } from '@/components/layout/footer';
import { SidebarLayout } from '@/components/layout/sidebar-layout';
import { GuestCreditOfferDialog } from '@/components/shared/guest-credit-offer-dialog';
import { GoogleOneTap } from '@/components/auth/google-one-tap';
import { CheckoutReturnOfferDialog } from '@/components/shared/checkout-return-offer-dialog';
import { PostGenerationCreditOfferDialog } from '@/components/shared/post-generation-credit-offer-dialog';
import { GoogleAdsFirstGenerationTracker } from '@/analytics/google-ads-first-generation';
import { GoogleAdsBeginCheckoutTracker } from '@/analytics/google-ads-begin-checkout';
import { GoogleAdsPurchaseTracker } from '@/analytics/google-ads-purchase';
import { GoogleAdsSignupTracker } from '@/analytics/google-ads-signup';
import { AttributionTracker } from '@/attribution/attribution-tracker';
import { getClarityInlineHtml } from '@/components/analytics/clarity-script';
import { websiteConfig } from '@/config/website';
import appCss from '../styles.css?url';
import { clientEnv } from '@/env/client';
import { DefaultCatchBoundary } from '@/components/layout/default-catch-boundary';
import { Routes } from '@/lib/routes';
import { getCanonicalUrl, getOgImage, twitterHandleFromUrl } from '@/lib/urls';
import {
  getAlternateLocales,
  getCanonicalPathname,
  getLocale,
  localeConfig,
} from '@/lib/locale';
import { Suspense, lazy, useEffect, useState } from 'react';

const LazyToaster = lazy(() =>
  import('@/components/shared/toaster').then((module) => ({
    default: module.Toaster,
  }))
);

const LazyAnalytics = lazy(() =>
  import('@/components/analytics/analytics').then((module) => ({
    default: module.Analytics,
  }))
);

const LazyCrispChat = lazy(() =>
  import('@/components/chatbox/crisp-chat').then((module) => ({
    default: module.CrispChat,
  }))
);

const DevTools = import.meta.env.DEV
  ? lazy(() => import('@/integrations/devtools'))
  : () => null;

/** Set to true to enable TanStack DevTools in development */
const ENABLE_DEVTOOLS = false;

/**
 * https://github.com/backpine/tanstack-start-on-cloudflare/blob/main/src/routes/__root.tsx
 */
export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  head: () => {
    const ogImage = getOgImage();
    const twitterSite = websiteConfig.social?.twitter
      ? twitterHandleFromUrl(websiteConfig.social.twitter)
      : null;
    const currentLocale = getLocale();
    // OG locale format uses underscore (e.g. en_US, zh_CN), unlike BCP 47 used
    // for <html lang> / hreflang which uses hyphens.
    const ogLocale = localeConfig[currentLocale].hreflang.replace('-', '_');
    const alternateOgLocales = getAlternateLocales(currentLocale).map((l) =>
      localeConfig[l].hreflang.replace('-', '_')
    );
    const clarityProjectId = import.meta.env.PROD
      ? clientEnv.VITE_CLARITY_PROJECT_ID
      : null;

    return {
      meta: [
        { charSet: 'utf-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        { title: websiteConfig.metadata?.title },
        { name: 'description', content: websiteConfig.metadata?.description },
        { name: 'theme-color', content: '#09090b' },
        // Default OG / Twitter / canonical — pages with their own head()
        // override these with page-specific values. These ensure 404 / error
        // pages and any future route that forgets to call seo() still get
        // shareable social metadata.
        { property: 'og:type', content: 'website' },
        {
          property: 'og:site_name',
          content: websiteConfig.metadata?.name ?? '',
        },
        { property: 'og:locale', content: ogLocale },
        ...alternateOgLocales.map((loc) => ({
          property: 'og:locale:alternate',
          content: loc,
        })),
        { property: 'og:title', content: websiteConfig.metadata?.title ?? '' },
        {
          property: 'og:description',
          content: websiteConfig.metadata?.description ?? '',
        },
        { property: 'og:url', content: getCanonicalUrl('/') },
        ...(ogImage ? [{ property: 'og:image', content: ogImage }] : []),
        { name: 'twitter:title', content: websiteConfig.metadata?.title ?? '' },
        ...(twitterSite
          ? [{ name: 'twitter:site', content: twitterSite }]
          : []),
        {
          name: 'twitter:description',
          content: websiteConfig.metadata?.description ?? '',
        },
        ...(ogImage
          ? [
              { name: 'twitter:card', content: 'summary_large_image' as const },
              { name: 'twitter:image', content: ogImage },
            ]
          : []),
      ],
      links: [
        {
          rel: 'stylesheet',
          href: appCss,
          'data-clarity-unmask': 'true',
        },
        {
          rel: 'apple-touch-icon',
          sizes: '180x180',
          href: '/apple-touch-icon.png?v=2',
        },
        {
          rel: 'icon',
          type: 'image/png',
          sizes: '32x32',
          href: '/favicon-32x32.png?v=2',
        },
        {
          rel: 'icon',
          type: 'image/png',
          sizes: '16x16',
          href: '/favicon-16x16.png?v=2',
        },
        { rel: 'icon', href: '/favicon.ico?v=2' },
        { rel: 'manifest', href: '/manifest.json' },
      ],
      scripts: clarityProjectId
        ? [
            {
              id: 'clarity-analytics',
              children: getClarityInlineHtml(clarityProjectId),
            },
          ]
        : [],
    };
  },
  // shellComponent automatically wraps root component, errorComponent, and notFoundComponent
  shellComponent: RootDocument,
  component: RootComponent,
  notFoundComponent: DefaultNotFound,
  errorComponent: DefaultCatchBoundary,
});

/**
 * Root component (wrapped by shellComponent: RootDocument).
 * SidebarLayout owns the shared footer for normal routes; auth and 404 routes
 * use the same footer below their minimal full-screen layout.
 */
function RootComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname }) ?? '';
  const canonicalPathname = getCanonicalPathname(pathname);
  const matches = useRouterState({ select: (s) => s.matches }) ?? [];
  const hasAuthRouteMatch = matches.some((match) => match.routeId === '/auth');
  const hasOnlyRootMatch = matches.length <= 1;
  const isAuthPages =
    hasAuthRouteMatch || canonicalPathname.startsWith(Routes.Auth);
  // When no child route matches (e.g. /hello), only root is in matches; use minimal layout
  const isNotFound =
    canonicalPathname !== Routes.Root &&
    canonicalPathname !== '' &&
    hasOnlyRootMatch;

  // Minimal full-screen layout for auth pages and 404.
  if (isAuthPages || isNotFound) {
    return (
      <div className="flex min-h-screen flex-col">
        <main id="main-content" className="flex-1">
          <Outlet />
        </main>
        <Footer className="shrink-0" />
      </div>
    );
  }

  return (
    <SidebarLayout>
      <GuestCreditOfferDialog />
      <CheckoutReturnOfferDialog />
      <PostGenerationCreditOfferDialog />
      <Outlet />
    </SidebarLayout>
  );
}

/**
 * Root document
 */
function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html
      dir={localeConfig[getLocale()].dir ?? 'ltr'}
      lang={localeConfig[getLocale()].hreflang}
      suppressHydrationWarning
    >
      <head>
        <HeadContent />
      </head>
      <body>
        <ThemeProvider>
          {children}
          <GoogleOneTap />
          <AttributionTracker />
          <GoogleAdsSignupTracker />
          <GoogleAdsFirstGenerationTracker />
          <GoogleAdsBeginCheckoutTracker />
          <GoogleAdsPurchaseTracker />
          <DeferredToaster />
          <DeferredRootUtilities />
        </ThemeProvider>
        {ENABLE_DEVTOOLS && <DevTools />}
        <Scripts />
      </body>
    </html>
  );
}

function DeferredRootUtilities() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    type RIC = (callback: () => void, options?: { timeout?: number }) => number;
    const requestIdle = (window as unknown as { requestIdleCallback?: RIC })
      .requestIdleCallback;
    if (typeof requestIdle === 'function') {
      const handle = requestIdle(() => setEnabled(true), { timeout: 4000 });
      return () => {
        const cancelIdle = (
          window as unknown as {
            cancelIdleCallback?: (handle: number) => void;
          }
        ).cancelIdleCallback;
        cancelIdle?.(handle);
      };
    }

    const handle = window.setTimeout(() => setEnabled(true), 2500);
    return () => window.clearTimeout(handle);
  }, []);

  if (!enabled) return null;

  return (
    <Suspense fallback={null}>
      <LazyAnalytics />
      <LazyCrispChat />
    </Suspense>
  );
}

function DeferredToaster() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    type RIC = (callback: () => void, options?: { timeout?: number }) => number;
    const requestIdle = (window as unknown as { requestIdleCallback?: RIC })
      .requestIdleCallback;
    if (typeof requestIdle === 'function') {
      const handle = requestIdle(() => setEnabled(true), { timeout: 3000 });
      return () => {
        const cancelIdle = (
          window as unknown as {
            cancelIdleCallback?: (handle: number) => void;
          }
        ).cancelIdleCallback;
        cancelIdle?.(handle);
      };
    }

    const handle = window.setTimeout(() => setEnabled(true), 2000);
    return () => window.clearTimeout(handle);
  }, []);

  if (!enabled) return null;

  return (
    <Suspense fallback={null}>
      <LazyToaster richColors position="top-right" offset={64} />
    </Suspense>
  );
}
